/**
 * The agents menu entry's ONE assembly of its inputs, as a hook.
 *
 * The owner, 2026-10-05 (08:49:17Z and 08:50:31Z): a caller with exactly one
 * agent who may not create another reads the SINGULAR agent noun on the menu
 * entry and goes straight into that agent; a caller who may create keeps the
 * plural label and the chooser. The decision is `resolveSingleAgentTarget` /
 * `resolveChooserRedirect` in landing.ts (pure, tested with object literals);
 * this hook only feeds them, so the sidebar entry (label + target) and the
 * `/agents` route (the replace on a direct visit) can never answer differently
 * for the same caller.
 *
 * Its reads are shared cache entries, never second fetchers
 * (SPEC-FRONTEND-RULE-077, QUERY-KEY-GATE): the posture through
 * `agentPostureQueryOptions` and the agent list through
 * `tenantAgentOptionsQueryOptions` - the list the landing chain and the header
 * logo already resolve against (GET /api/v1/agents, every page of it), so
 * "exactly one agent" is counted the way the landing counts it.
 *
 * FAIL CLOSED: only a SUCCESSFUL read decides (`isSuccess`, not `data`: a
 * query that failed after an earlier success still holds the old answer, the
 * case landingRedirect.test.tsx case 3 pins for the landing). While either read
 * is in flight or has failed, and while the permissions or the advanced-page
 * switch are still being read (the same two waits `LandingRedirect` honours),
 * the answer is "as before": the plural label and the chooser.
 *
 * @module apps/tenant/useAgentsMenuTarget
 */
import { useQuery } from "@tanstack/react-query";

import { agentPostureQueryOptions, tenantAgentOptionsQueryOptions } from "../../api/agentPosture";
import { usePermissionContext } from "../../store/PermissionContext";
import { usePagePresence } from "../../surfaces";
import { resolveChooserRedirect, resolveSingleAgentTarget } from "./landing";
import { useAgentStart, useLandingPredicate } from "./useLandingPredicate";

export interface AgentsMenuTarget {
  /**
   * The absolute path inside the caller's one agent when the menu entry stands
   * for it (singular label, link straight in); `null` = as before.
   */
  single: string | null;
  /**
   * Where a visit of `/agents` itself is replaced to; `null` = render the
   * chooser. Differs from `single` only for a caller who can manage the
   * tenant's members (D-SCA-10, see `resolveChooserRedirect`).
   */
  chooserRedirect: string | null;
}

const AS_BEFORE: AgentsMenuTarget = { single: null, chooserRedirect: null };

export function useAgentsMenuTarget(): AgentsMenuTarget {
  const { loading } = usePermissionContext();
  const { pending: switchPending } = usePagePresence();
  const usable = useLandingPredicate();
  const agentStart = useAgentStart();
  const posture = useQuery(agentPostureQueryOptions());
  const agents = useQuery(tenantAgentOptionsQueryOptions());
  if (loading || switchPending) return AS_BEFORE;
  const inputs = {
    posture: posture.isSuccess ? posture.data : null,
    agents: agents.isSuccess ? agents.data : null,
    usable,
    agentStart,
  };
  return {
    single: resolveSingleAgentTarget(inputs),
    chooserRedirect: resolveChooserRedirect(inputs),
  };
}
